import { isolatedAxios } from "./api"; // Ensure it's imported correctly
import { LOGIN_SUCCESS, LOGOUT } from "./actions/types";
import * as ServiceAPI from "./services/auth.service";
import { AuthHeader } from "./services/auth.header";

const Interceptor = (store) => {
  const shouldIntercept = (error) => {
    return error.response && error.response.status === 401;
  };

  const setTokenData = (tokenData = {}, axiosClient = null) => {
    if (Object.keys(tokenData).length === 0) return;
    if (axiosClient) {
      axiosClient.defaults.headers.common["Authorization"] = `Bearer ${tokenData.access_token}`;
    }
  };

  const handleTokenRefresh = async () => {
    const storeState = store.getState();
    const currentAccessToken = storeState?.auth.user.access_token;
    const currentRefreshToken = storeState?.auth.user.refresh_token;

    if (!currentAccessToken || !currentRefreshToken) {
      return Promise.reject(new Error("No tokens available"));
    }

    try {
      const response = await isolatedAxios.post(`/refresh-token`, {
        accessToken: currentAccessToken,
        refreshToken: currentRefreshToken
      });
      const data = response.data;
      const updatedUser = { ...storeState.auth?.user, ...data };
      store.dispatch({
        type: LOGIN_SUCCESS,
        payload: { user: updatedUser }
      });

      return data;
    } catch (error) {
      ServiceAPI.logout();
      store.dispatch({ type: LOGOUT });
      return Promise.reject(error);
    }
  };

  const applyInterceptor = (axiosClient) => {
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, token = null) => {
      failedQueue.forEach((prom) => {
        error ? prom.reject(error) : prom.resolve(token);
      });
      failedQueue = [];
    };

    // 1. Request Interceptor (Attached to the CLIENT, not global _axios)
    axiosClient.interceptors.request.use(
      (config) => {
        const auth = AuthHeader();

        if (auth?.Authorization) {
          config.headers = {
            ...config.headers,
            Authorization: auth.Authorization
          };
        }

        return config;
      },
      (error) => Promise.reject(error)
    );

    // 2. Response Interceptor
    axiosClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (!shouldIntercept(error) || originalRequest._retry) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = "Bearer " + token;
              return axiosClient(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          handleTokenRefresh()
            .then((tokenData) => {
              const accessToken = tokenData.access_token;
              setTokenData(tokenData, axiosClient);
              originalRequest.headers["Authorization"] = "Bearer " + accessToken;
              processQueue(null, accessToken);
              resolve(axiosClient(originalRequest));
            })
            .catch((err) => {
              processQueue(err, null);
              reject(err);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }
    );
  };

  return { applyInterceptor };
};

export default Interceptor;
